import '@schmancy/primitives/file-viewer'
import '@schmancy/primitives/link/link'
import '../chips/input-chip'
import { componentText, HostAriaLabelMixin, SchmancyElement } from '@schmancy/mixins/index'
import { show } from '@schmancy/overlay/overlay.service'
import { css, html } from 'lit'
import { customElement, property } from 'lit/decorators.js'
import { repeat } from 'lit/directives/repeat.js'
import { defer, EMPTY, exhaustMap, finalize, Subject, takeUntil } from 'rxjs'

/** The file a chip's remove control asked to leave, and where it stood. */
export type SchmancyFileChipsRemoveEvent = CustomEvent<{ file: File; index: number }>

/** The file a chip's name asked to preview, where it stood, and the files it pages among. Cancelable. */
export type SchmancyFileChipsPreviewEvent = CustomEvent<{ file: File; index: number; files: readonly File[] }>

/**
 * @element schmancy-file-chips
 *
 * The files a draft holds, one removable chip each. A chip's name opens the
 * file in `<schmancy-file-viewer>`, paged among the others; its remove control
 * fires `remove`. The consumer owns the list: this renders `files` and never
 * edits it.
 *
 * Preview reads the `File` itself through an object URL made when the viewer
 * opens and revoked when it closes, so nothing outlives the look. A consumer
 * that shows the preview in its own overlay cancels `preview`; the chips then
 * open nothing and make no object URL.
 *
 * @fires preview - `{ file, index, files }` — a chip's name was pressed; cancelable.
 * @fires remove - `{ file, index }` — the chip's remove control was pressed.
 */
@customElement('schmancy-file-chips')
export class SchmancyFileChips extends HostAriaLabelMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
				min-width: 0;
			}
		`,
	]

	@property({ attribute: false }) files: readonly File[] = []

	private readonly preview$ = new Subject<number>()

	override connectedCallback(): void {
		super.connectedCallback()
		this.preview$
			.pipe(
				exhaustMap(index =>
					defer(() => {
						// Asked only when the chips would open the viewer, so a press dropped
						// while one is open never reaches the consumer either.
						if (
							!this.dispatchEvent(
								new CustomEvent('preview', {
									detail: { file: this.files[index], index, files: this.files },
									bubbles: true,
									composed: true,
									cancelable: true,
								}),
							)
						)
							return EMPTY
						const shown = this.files.map(file => ({
							name: file.name,
							mime: file.type || 'application/octet-stream',
							size: file.size,
							previewUrl: URL.createObjectURL(file),
						}))
						return show(
							() => html`<section class="w-xl max-w-full h-[min(36rem,75dvh)] min-h-0">
								<schmancy-file-viewer class="block h-full max-w-full" .files=${shown} .index=${index}></schmancy-file-viewer>
							</section>`,
							{ ariaLabel: shown[index]?.name ?? componentText('fileChips.files') },
						).pipe(finalize(() => shown.forEach(item => URL.revokeObjectURL(item.previewUrl))))
					}),
				),
				takeUntil(this.disconnecting),
			)
			.subscribe()
	}

	override render() {
		return html`<ul
			class="m-0 flex min-w-0 list-none flex-wrap gap-2 p-0"
			aria-label=${this.ariaLabel || componentText('fileChips.files')}
		>
			${repeat(
				this.files,
				file => file,
				(file, index) => html`<li class="min-w-0">
					<schmancy-input-chip
						.value=${file.name}
						@remove=${(event: Event) => {
							event.stopPropagation()
							this.dispatchEvent(new CustomEvent('remove', { detail: { file, index }, bubbles: true, composed: true }))
						}}
						><schmancy-link standalone @press=${() => this.preview$.next(index)}>${file.name}</schmancy-link></schmancy-input-chip
					>
				</li>`,
			)}
		</ul>`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-file-chips': SchmancyFileChips
	}
}
